import React from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {Alert, Button} from 'react-bootstrap';

import {FmlxToast} from 'fmlx-common-ui';
import {Feedback} from '../../constants/dataEnum';
import {sendError} from '../../stores/business/errorReportingBusiness';

import withIntl from '../../lib/withIntl';

const ErrorPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const LogoIcon = styled.img`
  width: 44px;
`;

const LogoText = styled.img`
  width: 218px;
`;

const MessageTitle = styled.div`
  font-size: 18px;
`;

const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  font-size: 14px;
`;

const ActionButton = styled(Button)`
  width: 200px;
  height: 32px;
  font-size: 16px !important;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
`;

const StackButtons = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 20px;
`;

const StackTrace = styled.div`
  display: flex;
  height: calc(100vh - 500px);
`;

const mapDispatchToProps = (dispatch) => ({
  errorReportingBusinessSendError: bindActionCreators(sendError, dispatch),
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      info: null,
      showDetails: false,
      showNotification: false,
      notificationText: '',
      notificationStyle: '',
      logSent: false,
    };
  }

  componentDidMount() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const forceError = urlSearchParams.get('error') === 'true';
    if (forceError) {
      const err = new Error('Error triggered');
      setTimeout(() => {
        this.setState({
          hasError: true,
          error: err,
        });
      }, 6000);
      // You can also log the error to an error reporting service
      console.error('ErrorBoundary error', err);
    }
  }

  componentDidCatch(error, info) {
    // Display fallback UI
    this.setState({
      hasError: true,
      error,
      info,
    });

    // You can also log the error to an error reporting service
    console.error('ErrorBoundary error', error);
    console.error('ErrorBoundary info', info);
  }

  closeNotification = () => {
    this.setState({
      showNotification: false,
    });
  };

  // use this method to send an error to backend
  sendErrorReportHandler = async () => {
    const {errorReportingBusinessSendError, intl} = this.props;

    const result = await errorReportingBusinessSendError();

    // FastErrorCode.NoError
    if (result === 0) {
      this.setState({
        logSent: true,
        showNotification: true,
        notificationText: intl.formatMessage({id: 'downloadLog.sendEmailSuccess'}),
        notificationStyle: Feedback.SUCCESS,
      });

      setTimeout(() => {
        this.setState({
          showNotification: false,
        });
      }, 3000);

      return;
    }

    this.setState({
      logSent: true,
      showNotification: true,
      notificationText: intl.formatMessage({id: 'downloadLog.sendEmailError'}),
      notificationStyle: Feedback.ERROR,
    });

    setTimeout(this.closeNotification, 3000);
  };

  render() {
    const {hasError, error, info, showDetails, showNotification, notificationText, notificationStyle, logSent} =
      this.state;
    const {children} = this.props;

    if (hasError) {
      if (!logSent) {
        this.sendErrorReportHandler();
      }

      return (
        <div className="bootstrap-iso">
          <ErrorPage>
            <LogoIcon src="./svg/login/fast-icon.svg" alt="fast logo" />
            <br />
            <LogoText src="./svg/login/fast-text.svg" alt="fast logo" />
            <br />
            <br />
            <MessageTitle>
              <b>Something went wrong</b>
            </MessageTitle>
            <br />
            <MessageContent>
              <div>There is an error that cannot be resolved. Please restart your</div>
              <div>system in order to fix this problem. Contact</div>
              <div>
                <b>support@formulatrix.com</b>
                <span> if the problem persist.</span>
              </div>
            </MessageContent>
            <br />
            <br />
            <Details>
              <StackButtons>
                <ActionButton
                  className="button-primary"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  RESTART
                </ActionButton>
                <ActionButton
                  className="button-tertiary"
                  onClick={() => {
                    this.setState({showDetails: true});
                  }}
                >
                  SEE DETAILS
                </ActionButton>
              </StackButtons>
              <br />
              {showDetails && (
                <StackTrace>
                  <Alert
                    bsStyle="danger"
                    onDismiss={() => {
                      this.setState({showDetails: false});
                    }}
                    style={{overflow: 'auto'}}
                  >
                    <div>{error.message}</div>
                    <br />
                    <div>{error.stack}</div>
                    <div>{error.stack}</div>
                    <div>{error.stack}</div>
                    <br />
                    <div>{info?.componentStack}</div>
                  </Alert>
                </StackTrace>
              )}
            </Details>
            <FmlxToast
              open={showNotification}
              content={notificationText}
              variant={notificationStyle}
              onClick={this.closeNotification}
            />
          </ErrorPage>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.any.isRequired,
  intl: PropTypes.any.isRequired,
  errorReportingBusinessSendError: PropTypes.any.isRequired,
};

export default connect(null, mapDispatchToProps)(withIntl(ErrorBoundary));
